import { supabase } from '@/lib/supabaseClient'
import type { AuthAccount, AuthCallbackInput } from '@/types/auth'

function account(user: { id: string; email?: string } | null | undefined): AuthAccount | null {
  return user ? { id: user.id, email: user.email ?? '' } : null
}

export async function getAuthSession(): Promise<AuthAccount | null> {
  const { data, error } = await supabase.auth.getSession()
  if (error) throw error
  return account(data.session?.user)
}

export function subscribeAuthSession(changed: (value: AuthAccount | null) => void): () => void {
  // Keep this callback synchronous: auth notifications run inside the SDK lock.
  const { data } = supabase.auth.onAuthStateChange((event, session) => {
    // Bootstrap owns the initial read/error. The SDK can report INITIAL_SESSION
    // with null after a read error, which must not masquerade as signed-out.
    if (event !== 'INITIAL_SESSION') changed(account(session?.user))
  })
  return () => data.subscription.unsubscribe()
}

export async function signIn(email: string, password: string): Promise<AuthAccount> {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) throw error
  const identity = account(data.session?.user)
  if (!identity) throw new Error('Sign-in did not establish a session.')
  return identity
}

export async function signOut(): Promise<void> {
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}

export async function completeAuthCallback(input: AuthCallbackInput): Promise<AuthAccount> {
  const response = input.accessToken && input.refreshToken
    ? await supabase.auth.setSession({ access_token: input.accessToken, refresh_token: input.refreshToken })
    : input.code ? await supabase.auth.exchangeCodeForSession(input.code) : null
  if (response?.error) throw response.error
  const identity = response ? account(response.data.session?.user) : await getAuthSession()
  if (!identity) throw new Error('Invite link is invalid or expired. Please request access again.')
  return identity
}
